'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Timer, Play, Pause, RotateCcw, ChevronDown } from 'lucide-react';

interface PomodoroTimerProps {
  currentTopic: string;
}

type TimerMode = 'focus' | 'break';

export default function PomodoroTimer({ currentTopic }: PomodoroTimerProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [mode, setMode] = useState<TimerMode>('focus');
  const [focusMins, setFocusMins] = useState(25);
  const [breakMins, setBreakMins] = useState(5);
  const [secondsLeft, setSecondsLeft] = useState(25 * 60);
  const [isRunning, setIsRunning] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const totalSeconds = mode === 'focus' ? focusMins * 60 : breakMins * 60;
  const pct = ((totalSeconds - secondsLeft) / totalSeconds) * 100;

  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  const display = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    stop();
    intervalRef.current = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          stop();
          setIsRunning(false);
          // Switch mode
          setMode(m => {
            const next = m === 'focus' ? 'break' : 'focus';
            setSecondsLeft(next === 'focus' ? focusMins * 60 : breakMins * 60);
            return next;
          });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [stop, focusMins, breakMins]);

  useEffect(() => {
    if (isRunning) start();
    else stop();
    return stop;
  }, [isRunning, start, stop]);

  const handleReset = () => {
    setIsRunning(false);
    stop();
    setSecondsLeft(mode === 'focus' ? focusMins * 60 : breakMins * 60);
  };

  const circumference = 2 * Math.PI * 20;
  const offset = circumference - (pct / 100) * circumference;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '90px',
        right: '24px',
        zIndex: 40,
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          overflow: 'hidden',
          transition: 'all 200ms ease',
          width: isExpanded ? '260px' : '56px',
        }}
      >
        {/* Collapsed: just icon + time */}
        {!isExpanded ? (
          <button
            onClick={() => setIsExpanded(true)}
            aria-label="Open Pomodoro timer"
            style={{
              width: '56px',
              height: '56px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: isRunning ? 'var(--accent)' : 'var(--text-muted)',
              position: 'relative',
            }}
          >
            <svg width="44" height="44" viewBox="0 0 44 44" style={{ transform: 'rotate(-90deg)', position: 'absolute' }}>
              <circle cx="22" cy="22" r="20" fill="none" stroke="var(--border)" strokeWidth="2" />
              <circle
                cx="22"
                cy="22"
                r="20"
                fill="none"
                stroke="var(--accent)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                style={{ transition: 'stroke-dashoffset 1s linear', opacity: isRunning ? 1 : 0.4 }}
              />
            </svg>
            <Timer size={18} style={{ position: 'relative', zIndex: 1 }} />
          </button>
        ) : (
          <div style={{ padding: '14px' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Timer size={14} style={{ color: 'var(--accent)' }} />
                <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', fontFamily: 'IBM Plex Mono, monospace' }}>
                  POMODORO
                </span>
              </div>
              <button
                onClick={() => setIsExpanded(false)}
                aria-label="Collapse Pomodoro timer"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: '2px' }}
              >
                <ChevronDown size={15} />
              </button>
            </div>

            {/* Mode toggle */}
            <div style={{ display: 'flex', gap: '4px', marginBottom: '12px', backgroundColor: 'var(--bg-elevated)', borderRadius: '6px', padding: '3px' }}>
              {(['focus', 'break'] as TimerMode[]).map(m => (
                <button
                  key={`pomo-mode-${m}`}
                  onClick={() => {
                    if (!isRunning) {
                      setMode(m);
                      setSecondsLeft(m === 'focus' ? focusMins * 60 : breakMins * 60);
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '5px',
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: mode === m ? 'var(--bg-card)' : 'transparent',
                    color: mode === m ? 'var(--text-primary)' : 'var(--text-muted)',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: 600,
                    transition: 'all 150ms ease',
                    fontFamily: 'IBM Plex Mono, monospace',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                  }}
                >
                  {m === 'focus' ? 'Focus' : 'Break'}
                </button>
              ))}
            </div>

            {/* Circular timer */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px', position: 'relative' }}>
              <svg width="100" height="100" viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)' }}>
                <circle cx="50" cy="50" r="42" fill="none" stroke="var(--bg-elevated)" strokeWidth="5" />
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  fill="none"
                  stroke={mode === 'focus' ? 'var(--accent)' : '#9B96F7'}
                  strokeWidth="5"
                  strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 42}
                  strokeDashoffset={2 * Math.PI * 42 - (pct / 100) * 2 * Math.PI * 42}
                  style={{ transition: 'stroke-dashoffset 1s linear' }}
                />
              </svg>
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: '22px', fontWeight: 600, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                  {display}
                </div>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: 'IBM Plex Mono, monospace' }}>
                  {mode}
                </div>
              </div>
            </div>

            {/* Topic */}
            {currentTopic && (
              <div
                style={{
                  fontSize: '11px',
                  color: 'var(--text-muted)',
                  textAlign: 'center',
                  marginBottom: '10px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  padding: '0 4px',
                }}
              >
                {currentTopic}
              </div>
            )}

            {/* Controls */}
            <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
              <button
                onClick={() => setIsRunning(v => !v)}
                aria-label={isRunning ? 'Pause timer' : 'Start timer'}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '8px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: mode === 'focus' ? 'var(--accent)' : '#534AB7',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 600,
                  transition: 'all 150ms ease',
                }}
              >
                {isRunning ? <Pause size={14} /> : <Play size={14} />}
                {isRunning ? 'Pause' : 'Start'}
              </button>
              <button
                onClick={handleReset}
                aria-label="Reset timer"
                style={{
                  padding: '8px 10px',
                  borderRadius: '6px',
                  border: '1px solid var(--border)',
                  backgroundColor: 'var(--bg-elevated)',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  display: 'flex',
                  transition: 'all 150ms ease',
                }}
              >
                <RotateCcw size={14} />
              </button>
            </div>

            {/* Custom duration */}
            <div style={{ marginTop: '10px', display: 'flex', gap: '6px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'IBM Plex Mono, monospace', display: 'block', marginBottom: '3px' }}>
                  FOCUS
                </label>
                <input
                  type="number"
                  min={1}
                  max={90}
                  value={focusMins}
                  onChange={e => {
                    const v = parseInt(e.target.value, 10);
                    if (v > 0) {
                      setFocusMins(v);
                      if (mode === 'focus' && !isRunning) setSecondsLeft(v * 60);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '4px 6px',
                    borderRadius: '4px',
                    border: '1px solid var(--border)',
                    backgroundColor: 'var(--bg-input)',
                    color: 'var(--text-primary)',
                    fontSize: '12px',
                    fontFamily: 'IBM Plex Mono, monospace',
                    outline: 'none',
                    textAlign: 'center',
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'IBM Plex Mono, monospace', display: 'block', marginBottom: '3px' }}>
                  BREAK
                </label>
                <input
                  type="number"
                  min={1}
                  max={30}
                  value={breakMins}
                  onChange={e => {
                    const v = parseInt(e.target.value, 10);
                    if (v > 0) {
                      setBreakMins(v);
                      if (mode === 'break' && !isRunning) setSecondsLeft(v * 60);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '4px 6px',
                    borderRadius: '4px',
                    border: '1px solid var(--border)',
                    backgroundColor: 'var(--bg-input)',
                    color: 'var(--text-primary)',
                    fontSize: '12px',
                    fontFamily: 'IBM Plex Mono, monospace',
                    outline: 'none',
                    textAlign: 'center',
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}